import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { SiteUpdateAttributes } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { queryKeys } from "~/api/queryKeys";
import { AiVisibilityOptimizationsRHFAdapter } from "~/components/siteSettings/AiVisibilityOptimizationsRHFAdapter";
import type { SiteSettingsFormValues } from "~/components/siteSettings/SiteSettings";
import wizardStyles from "~/components/siteSpeed/wizzard/Wizzard.module.scss";
import { useSite } from "~/hooks/useSite";
import { routes } from "~/router";

import Button from "../../ui/button/Button";
import { tabs } from "./NewSite";

const FORM_ID = "newSiteVisibilityForm";

type NewSiteVisibilitySettingsProps = {
  newSiteId: number;
  setActiveTab: (tab: string) => void;
};

const NewSiteVisibilitySettings = ({
  newSiteId,
  setActiveTab,
}: NewSiteVisibilitySettingsProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: siteData, isLoading } = useSite(newSiteId);
  const site = siteData?.site;

  const methods = useForm<SiteSettingsFormValues>();
  const {
    handleSubmit,
    reset,
    formState: { isDirty },
  } = methods;

  // Initialize the form once the site is available. Key the reset on the stable
  // site id (not the whole object) so a siteDetails invalidation/refetch doesn't
  // re-run reset and wipe the user's toggles.
  useEffect(() => {
    if (!site) return;

    reset({
      // This step is WordPress-only: prerendering is backend-managed (always
      // on), so the field is hidden and never submitted. Seed it enabled so
      // the dependent toggles below it aren't gated off.
      prerendering: true,
      apex_redirect: !!site.apex_redirect,
      // In WordPress onboarding, default JS snippet injection to enabled.
      inject_widget: true,
      inject_markdown: site.inject_markdown,
    });
  }, [site?.id, reset]);

  const finishToDashboard = () => {
    enqueueSnackbar("Site setup complete! Redirecting to dashboard...", {
      variant: "success",
    });

    localStorage.removeItem("siteId");
    navigate(routes.site.root({ id: newSiteId }));
  };

  const { mutate: updateSite, isPending: isUpdating } = useMutation({
    mutationFn: (data: SiteUpdateAttributes) =>
      apiClient.sites.sitesPartialUpdate(newSiteId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.siteDetails(newSiteId),
      });
      finishToDashboard();
    },
    onError: () => {
      enqueueSnackbar("Could not save settings", {
        variant: "error",
        persist: true,
      });
    },
  });

  const onFinish = (data: SiteSettingsFormValues) => {
    // This step force-enables inject_widget. Save when the user changed
    // something, or when the backend hasn't enabled it yet.
    const needsSave = isDirty || !site?.inject_widget;

    if (needsSave) {
      updateSite({
        apex_redirect: data.apex_redirect,
        inject_widget: data.inject_widget,
        inject_markdown: data.inject_markdown ?? undefined,
      });
    } else {
      finishToDashboard();
    }
  };

  return (
    <FormProvider {...methods}>
      <div className={wizardStyles.stepSpacing}>
        <div className={wizardStyles.stepContentTitle}>
          Configure AI Visibility Engine Settings
        </div>
        <form id={FORM_ID} onSubmit={handleSubmit(onFinish)}>
          <div
            className={`${wizardStyles.edgeInstallationDetailsWrapper} ${wizardStyles.installWrapper}`}
          >
            <div className={wizardStyles.edgeInstallationDetailsContent}>
              <AiVisibilityOptimizationsRHFAdapter
                siteData={siteData}
                isLoading={isLoading}
                hideInjectWidgetManualAlert
                hidePrerenderingToggle
                hideDisabledControls
              />
            </div>
          </div>
        </form>
        <div className={wizardStyles.stepContentFooter}>
          <Button
            outlined
            onClick={() => {
              setActiveTab(tabs[4]);
              navigate(tabs[4]);
            }}
          >
            Back
          </Button>
          <Button type="submit" formId={FORM_ID} withSpinner={isUpdating}>
            Finish
          </Button>
        </div>
      </div>
    </FormProvider>
  );
};

export default NewSiteVisibilitySettings;
